import {
  Button,
  Modal,
  ModalCloseIconButton,
  ModalContent,
  ModalTitle,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import type { ReactNode } from 'react';

type ButtonOptions = { label: string } & (
  | { href: string; onClick?: never }
  | { onClick: () => void; href?: never }
);

interface GenericModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  content: string | ReactNode;
  cancelButton: ButtonOptions;
  continueButton: ButtonOptions;
  testIdPrefix?: string;
}

const GenericModal: React.FC<GenericModalProps> = ({
  open,
  onClose,
  title,
  content,
  cancelButton,
  continueButton,
  testIdPrefix = 'generic-modal',
}) => {
  const contentIsString = typeof content === 'string';

  return (
    <Modal
      open={open}
      sx={{
        '& .MuiPaper-root': {
          maxWidth: 476,
        },
        textAlign: 'left',
        position: 'absolute',
        '& .MuiBackdrop-root': {
          position: 'absolute',
        },
      }}
      disablePortal
      data-testid={`${testIdPrefix}`}
    >
      <ModalCloseIconButton
        data-testid={`${testIdPrefix}-close-button`}
        onClick={onClose}
      />

      <ModalTitle
        id={`${testIdPrefix}-title`}
        data-testid={`${testIdPrefix}-title`}
        sx={{ fontWeight: 700, pr: 4 }}
      >
        {title}
      </ModalTitle>

      <ModalContent
        id={`${testIdPrefix}-content`}
        data-testid={`${testIdPrefix}-content`}
      >
        {contentIsString ? (
          <Typography variant='b2Regular'>{content}</Typography>
        ) : (
          content
        )}
      </ModalContent>

      <Stack direction='row' spacing={2} mt={2}>
        <ActionButton
          options={cancelButton}
          variant='outlined'
          testId={`${testIdPrefix}-cancel-button`}
        />
        <ActionButton
          options={continueButton}
          variant='contained'
          testId={`${testIdPrefix}-continue-button`}
        />
      </Stack>
    </Modal>
  );
};

export default GenericModal;

interface ActionButtonProps {
  options: ButtonOptions;
  variant: 'outlined' | 'contained';
  testId: string;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  options,
  variant,
  testId,
}) => {
  return (
    <Button
      data-testid={testId}
      variant={variant}
      label={options.label}
      href={'href' in options ? options.href : undefined}
      onClick={'onClick' in options ? options.onClick : undefined}
      fullWidth
    />
  );
};
